import { useMemo } from "react";

import { type ProductInterface, type ProductPrice } from "@jti/fe-common";
import {
  getIsInStock,
  getStockStatus,
  type IProductStockStatus,
  type ProductVariant,
  useProductDetails,
  useProductPromotionalOffersQuery,
  useStockStatuses,
} from "@jti/magento";
import type { ApiMagentoPriceTier } from "@jti/magento-types";

import { getConditionalPurchaseOffer } from "@product-details/hooks/useSpecialPromo";
import { getOptions, getPriceTiers, getSiblingItemsStockStatus, getSingleProductPrice } from "@product-details/utils";

import { type ProductData, type UseProductDataOptions } from "./useProductData.types";

const getMultipacksCount = (productData: ProductInterface) => {
  const QtyOfMultiItems = productData?.items
    ?.filter(({ type }: { type: string }) => type === "multi")
    .map(({ option_id, qty }: { option_id: number; qty: number }) => ({ option_id, qty }));

  return QtyOfMultiItems || [];
};

const getDefaultPriceTier = (priceInformation: ProductPrice): ApiMagentoPriceTier => ({
  final_price: priceInformation.finalPrice,
  total_price: priceInformation.finalPrice,
  quantity: 1,
  discount: {},
});

const findCurrentVariant = (
  productData: ProductInterface,
  isConfigurableProduct: boolean,
  currentVariantSku: string | undefined
): ProductVariant | undefined => {
  if (!isConfigurableProduct || !currentVariantSku) return undefined;

  return (productData?.variants as ProductVariant[] | undefined)?.find(
    (variant: ProductVariant) => variant.product?.sku === currentVariantSku
  );
};

export const useProductData = ({
  sku,
  pageSize = 1,
  isTrial = false,
  currentVariantSku,
  categoriesSKUList = [],
  isDynamicGalleryEnabled = false,
}: UseProductDataOptions): ProductData => {
  const {
    data: getProductsResponseData,
    isFetching,
    isSuccess,
    discount,
    priceInformation,
  } = useProductDetails({
    sku,
    pageSize,
    isTrial,
    currentVariantSku,
  });

  const { data: siblingItemsStockStatus = {}, isFetching: isFetchingSiblingItemsStockStatus } = useStockStatuses<
    Record<string, IProductStockStatus>
  >({
    categoriesSKUList,
    isTrial,
    select: getSiblingItemsStockStatus,
  });

  const { data: promotionalOffersData } = useProductPromotionalOffersQuery({
    sku,
    pageSize,
    isTrial,
  });

  const [productData] = getProductsResponseData?.products?.items ?? [];

  const productType = productData?.__typename;

  const isBundle = productType === "BundleProduct";

  const isConfigurableProduct = productType === "ConfigurableProduct";

  const multipacksQuantities = useMemo(() => getMultipacksCount(productData), [productData]);

  const finalPrices = useMemo(() => {
    return productData?.price_range.minimum_price.final_prices ?? [];
  }, [productData]);

  const elementsStockStatus = useMemo(
    () =>
      isConfigurableProduct || isBundle
        ? (getOptions(productData, "stock") as Record<string, IProductStockStatus>)
        : {},
    [productData]
  );

  const elementsDynamicGalleryUrls = useMemo(
    () => (productData && isDynamicGalleryEnabled && isBundle ? getOptions(productData, "gallery") : {}),
    [productData]
  );

  const isInStock = useMemo(() => getIsInStock(currentVariantSku, productData), [productData]);
  const stockStatus = useMemo(() => getStockStatus(currentVariantSku, productData), [productData]);

  const priceTiers = useMemo(() => {
    return [getDefaultPriceTier(priceInformation), ...getPriceTiers(productData)];
  }, [productData, priceInformation]);

  const singleProductPrice = useMemo(() => getSingleProductPrice(productData), [productData]);

  const promotionalOffers = promotionalOffersData?.products?.items?.[0]?.promotionalOffers;
  const specialOffer = useMemo(() => getConditionalPurchaseOffer(promotionalOffers ?? []), [promotionalOffers]);

  const flavor = useMemo(() => {
    return productData?.flavor ?? "";
  }, [productData]);

  const currentVariant = useMemo(
    () => findCurrentVariant(productData, isConfigurableProduct, currentVariantSku),
    [productData, isConfigurableProduct, currentVariantSku]
  );

  const color = productData?.color ?? currentVariant?.product?.color ?? "";

  const subtitle = productData?.subtitle ?? "";

  const productDisclaimer = currentVariant?.product?.product_disclaimer ?? productData?.product_disclaimer ?? "";

  return {
    isProductDataLoading: isFetching,
    isProductDataLoaded: !isFetching && isSuccess,
    elementsStockStatus,
    siblingItemsStockStatus,
    isFetchingSiblingItemsStockStatus,
    isInStock,
    stockStatus,
    priceInformation,
    discount,
    priceTiers,
    singleProductPrice,
    elementsDynamicGalleryUrls,
    productType,
    specialOffer,
    multipacksQuantities,
    finalPrices,
    subtitle,
    productDisclaimer,
    color,
    flavor,
  };
};
